import { useImperativeHandle } from "react";
import { compositeEditorToPngBlob } from "../../utils/editorExportComposite";
import { getTransform } from "../../utils/getTransform";
import { computePaddedImageTransform } from "../../utils/canvasPaddingLayout";
import { clampPositionAllowOverflow } from "../../components/objectTransform/objectTransformMath";
import { normalizeLayers, useEditorStore } from "../../state/editorStore";
import { computeFittedCanvasSize } from "./computeFittedCanvasSize";
import { tightenLockedSubject } from "./subjectLayout";
import { consumePendingSubjectPlacement } from "./applySubjectPlacement";

export function useCanvasCommands(ref, ctx) {
  const {
    alphaMaskFallbackRef,
    alphaMaskRef,
    backgroundColor,
    backgroundLayerVisible,
    canvasReadyRef,
    canvasSizeRef,
    containerRef,
    cropExtentRef,
    cropStateRef,
    imageLoadedRef,
    imageUrl,
    layoutLockFullObjectRef,
    layoutLockRef,
    objectFlipRef,
    objectLayerVisible,
    objectPositionRef,
    objectRotationRef,
    objectSizeRef,
    objectTightBoundsRef,
    originalImageSizeRef,
    pendingSubjectPlacementRef,
    forceFullFramePendingRef,
    preserveLayoutPendingRef,
    preserveLayoutSnapshotRef,
    resolutionLabel,
    selectLayer,
    backgroundImageUrl,
    canvasSize,
    objectSize,
    outlineBleedPx,
    outlineImageUrl,
    outlineState,
    selectedElement,
    setBackgroundImageLoading,
    setBackgroundImageUrl,
    setCanvasCornerRadius,
    setCanvasSize,
    setCropState,
    setIsDragging,
    setIsResizing,
    setIsRotating,
    setObjectCornerRadii,
    setObjectFlip,
    setObjectPosition,
    setObjectRotation,
    setObjectSize,
    setOriginalImageSize,
    setOutlineState,
    setSelectedElement,
    setShadowState,
    shadowState,
  } = ctx;

  const retightenSubjectUnderLayoutLock = () => tightenLockedSubject({
    alphaMaskRef,
    alphaMaskFallbackRef,
    originalImageSizeRef,
    layoutLockFullObjectRef,
    objectSizeRef,
    objectPositionRef,
    cropStateRef,
    cropExtentRef,
    objectTightBoundsRef,
    setCropState,
    setObjectSize,
    setObjectPosition,
  });
  const placementLayout = () => ({
    pendingSubjectPlacementRef,
    canvasSizeRef,
    originalImageSizeRef,
    layoutLockRef,
    layoutLockFullObjectRef,
    cropStateRef,
    cropExtentRef,
    objectSizeRef,
    objectPositionRef,
    objectRotationRef,
    objectTightBoundsRef,
    setCropState,
    setObjectSize,
    setObjectPosition,
    setObjectRotation,
    retighten: retightenSubjectUnderLayoutLock,
  });
  // Expose alignment and rotation methods to parent via ref
  useImperativeHandle(ref, () => ({
    alignObjectMiddle: () => {
      // Center horizontally (middle)
      const newX = (canvasSize.width - objectSize.width) / 2;
      setObjectPosition((prev) => ({ ...prev, x: newX }));
    },
    alignObjectCenter: () => {
      // Center vertically (center)
      const newY = (canvasSize.height - objectSize.height) / 2;
      setObjectPosition((prev) => ({ ...prev, y: newY }));
    },
    alignObjectLeft: () => {
      // Align to left edge
      setObjectPosition((prev) => ({ ...prev, x: 0 }));
    },
    alignObjectRight: () => {
      // Align to right edge
      const newX = canvasSize.width - objectSize.width;
      setObjectPosition((prev) => ({ ...prev, x: newX }));
    },
    alignObjectTop: () => {
      // Align to top edge
      setObjectPosition((prev) => ({ ...prev, y: 0 }));
    },
    alignObjectBottom: () => {
      // Align to bottom edge
      const newY = canvasSize.height - objectSize.height;
      setObjectPosition((prev) => ({ ...prev, y: newY }));
    },
    alignObjectTopLeft: () => {
      // Align to top-left corner
      setObjectPosition({ x: 0, y: 0 });
    },
    alignObjectTopRight: () => {
      // Align to top-right corner
      const newX = canvasSize.width - objectSize.width;
      setObjectPosition({ x: newX, y: 0 });
    },
    alignObjectBottomLeft: () => {
      // Align to bottom-left corner
      const newY = canvasSize.height - objectSize.height;
      setObjectPosition({ x: 0, y: newY });
    },
    alignObjectBottomRight: () => {
      // Align to bottom-right corner
      const newX = canvasSize.width - objectSize.width;
      const newY = canvasSize.height - objectSize.height;
      setObjectPosition({ x: newX, y: newY });
    },
    setRotation: (angle) => {
      const next = Number(angle) || 0;
      if (objectRotationRef.current === next) return;
      objectRotationRef.current = next;
      setObjectRotation(next);
    },
    resetRotation: () => {
      objectRotationRef.current = 0;
      objectFlipRef.current = { x: 1, y: 1 };
      setObjectRotation(0);
      setObjectFlip({ x: 1, y: 1 });
    },
    flipHorizontal: () => {
      setObjectFlip((prev) => ({ ...prev, x: prev.x * -1 }));
    },
    flipVertical: () => {
      setObjectFlip((prev) => ({ ...prev, y: prev.y * -1 }));
    },
    setFlip: (flipH, flipV) => {
      const next = { x: flipH ? -1 : 1, y: flipV ? -1 : 1 };
      const prev = objectFlipRef.current;
      if (prev.x === next.x && prev.y === next.y) return;
      objectFlipRef.current = next;
      setObjectFlip(next);
    },
    getFlip: () => {
      const flip = objectFlipRef.current;
      return { flipH: flip.x < 0, flipV: flip.y < 0 };
    },
    getRotation: () => {
      return objectRotationRef.current;
    },
    selectObject: () => {
      setIsResizing(false);
      setIsRotating(false);
      setSelectedElement("object");
      selectLayer("object");
    },
    selectCanvas: () => {
      setIsDragging(false);
      setIsResizing(false);
      setIsRotating(false);
      setSelectedElement("canvas");
      selectLayer("background");
    },
    selectOverlay: (layerId) => {
      setIsDragging(false);
      setIsResizing(false);
      setIsRotating(false);
      setSelectedElement(null);
      selectLayer(layerId);
    },
    getCanvasSize: () => ({ ...canvasSizeRef.current }),
    getSelectedElement: () => selectedElement,
    /**
     * Next image swap keeps current canvas + object size/position. Opaque crop
     * stays tight for the selection BB; soft pixels (AI shadow) may paint outside.
     */
    setPreserveObjectLayoutOnce: () => {
      preserveLayoutPendingRef.current = true;
      preserveLayoutSnapshotRef.current = {
        canvasSize: { ...canvasSizeRef.current },
        objectSize: { ...objectSizeRef.current },
        objectPosition: { ...objectPositionRef.current },
      };
    },
    /**
     * Next image swap fills the canvas with the full bitmap (no opaque BB crop).
     * Used when Remove BG is toggled OFF to restore the uploaded photo.
     */
    setForceFullFrameLayoutOnce: () => {
      forceFullFramePendingRef.current = true;
      preserveLayoutPendingRef.current = false;
      preserveLayoutSnapshotRef.current = null;
    },
    // Shadow control methods (isolated feature)
    setShadowEnabled: (enabled) => {
      setShadowState((prev) =>
        prev.enabled === enabled ? prev : { ...prev, enabled }
      );
    },
    setShadowOffset: (offsetX, offsetY) => {
      setShadowState((prev) =>
        prev.offsetX === offsetX && prev.offsetY === offsetY
          ? prev
          : { ...prev, offsetX, offsetY }
      );
    },
    setShadowBlur: (blur) => {
      const next = Math.max(0, blur);
      setShadowState((prev) =>
        prev.blur === next ? prev : { ...prev, blur: next }
      );
    },
    setShadowOpacity: (opacity) => {
      const next = Math.max(0, Math.min(1, opacity));
      setShadowState((prev) =>
        prev.opacity === next ? prev : { ...prev, opacity: next }
      );
    },
    setShadowColor: (color) => {
      setShadowState((prev) =>
        prev.color === color ? prev : { ...prev, color }
      );
    },
    getShadowState: () => {
      return { ...shadowState };
    },
    // Outline control methods (isolated feature)
    setOutlineEnabled: (enabled) => {
      setOutlineState((prev) =>
        prev.enabled === enabled ? prev : { ...prev, enabled }
      );
    },
    setOutlineWidth: (width) => {
      const next = Math.max(0, width);
      setOutlineState((prev) =>
        prev.width === next ? prev : { ...prev, width: next }
      );
    },
    setOutlineBlur: (blur) => {
      const next = Math.max(0, blur);
      setOutlineState((prev) =>
        prev.blur === next ? prev : { ...prev, blur: next }
      );
    },
    setOutlineColor: (color) => {
      setOutlineState((prev) =>
        prev.color === color ? prev : { ...prev, color }
      );
    },
    getOutlineState: () => {
      return { ...outlineState };
    },
    // Background image control methods (locked, non-interactive base layer)
    setBackgroundImage: (imageUrl) => {
      // Async method to load and apply background image with proper safety
      setBackgroundImageLoading(true);
      
      if (!imageUrl) {
        setBackgroundImageUrl(null);
        setBackgroundImageLoading(false);
        return;
      }

      const img = new Image();
      if (
        typeof imageUrl === "string" &&
        /^https?:\/\//i.test(imageUrl) &&
        !imageUrl.startsWith("blob:") &&
        !imageUrl.startsWith("data:")
      ) {
        img.crossOrigin = "anonymous";
      }
      img.onload = () => {
        setBackgroundImageUrl(imageUrl);
        setBackgroundImageLoading(false);
      };
      img.onerror = () => {
        console.error("[CanvasEditor] Failed to load background image:", imageUrl);
        setBackgroundImageLoading(false);
      };
      img.src = imageUrl;
    },
    removeBackgroundImage: () => {
      setBackgroundImageUrl(null);
      setBackgroundImageLoading(false);
    },
    getBackgroundImageUrl: () => {
      return backgroundImageUrl;
    },
    resizeCanvasToAspectRatio: (aspectRatio) => {
      // Programmatic canvas resize based on aspect ratio (preset-driven)
      // Also scales and repositions image to fit within new canvas bounds
      if (!aspectRatio || aspectRatio <= 0) {
        console.error("[resizeCanvasToAspectRatio] Invalid aspect ratio:", aspectRatio);
        return;
      }

      const container = containerRef.current;
      if (!container) {
        console.error("[resizeCanvasToAspectRatio] Container ref not available");
        return;
      }

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      if (!containerWidth || !containerHeight) {
        console.error("[resizeCanvasToAspectRatio] Container has invalid dimensions");
        return;
      }

      console.log(`[resizeCanvasToAspectRatio] Resizing to aspect ratio ${aspectRatio.toFixed(4)}. Container: ${containerWidth}×${containerHeight}`);

      const fitted = computeFittedCanvasSize(containerWidth, containerHeight, aspectRatio);
      if (!fitted) {
        console.error("[resizeCanvasToAspectRatio] Container has invalid dimensions");
        return;
      }

      const newCanvasWidth = fitted.width;
      const newCanvasHeight = fitted.height;

      console.log(`[resizeCanvasToAspectRatio] New canvas size: ${newCanvasWidth}×${newCanvasHeight}`);

      // Calculate scaled image dimensions to fit within new canvas
      let newImgWidth = objectSize.width;
      let newImgHeight = objectSize.height;
      
      if (newImgWidth > newCanvasWidth || newImgHeight > newCanvasHeight) {
        const scaleX = newCanvasWidth / newImgWidth;
        const scaleY = newCanvasHeight / newImgHeight;
        const scale = Math.min(scaleX, scaleY);
        
        newImgWidth = newImgWidth * scale;
        newImgHeight = newImgHeight * scale;
        
        console.log(`[resizeCanvasToAspectRatio] Scaled image from ${objectSize.width}×${objectSize.height} to ${Math.round(newImgWidth)}×${Math.round(newImgHeight)}`);
      }

      // Update canvas and image size together
      setCanvasSize({ width: newCanvasWidth, height: newCanvasHeight });
      setObjectSize({ width: newImgWidth, height: newImgHeight });

      // Keep a sliver on-canvas; allow hanging off edges (content clips)
      setObjectPosition((prev) =>
        clampPositionAllowOverflow(
          prev.x,
          prev.y,
          newImgWidth,
          newImgHeight,
          newCanvasWidth,
          newCanvasHeight
        )
      );

      console.log("[resizeCanvasToAspectRatio] Canvas and image resized successfully.");
    },
    getCanvasDisplaySize: () => {
      const size = canvasSizeRef.current;
      return { width: size.width, height: size.height };
    },
    /**
     * Apply FIT/FILL object geometry from shared getTransform().
     * Allows overflow (negative x/y) for FILL center-crop.
     */
    applyObjectTransform: ({ width, height, x, y }) => {
      const w = Math.max(1, Number(width) || 0);
      const h = Math.max(1, Number(height) || 0);
      objectSizeRef.current = { width: w, height: h };
      setObjectSize({ width: w, height: h });
      setObjectPosition({
        x: Number.isFinite(Number(x)) ? Number(x) : 0,
        y: Number.isFinite(Number(y)) ? Number(y) : 0,
      });
      objectTightBoundsRef.current = false;
    },
    /**
     * Atomically set canvas AR + FIT/FILL transform (avoids race with resizeCanvasToAspectRatio FIT shrink).
     */
    applyPresetWithMode: ({ aspectRatio, mode = "fit", imageWidth, imageHeight }) => {
      if (!aspectRatio || aspectRatio <= 0) {
        console.error("[applyPresetWithMode] Invalid aspect ratio:", aspectRatio);
        return false;
      }
      const iw = Number(imageWidth);
      const ih = Number(imageHeight);
      if (!iw || !ih || iw <= 0 || ih <= 0) {
        console.error("[applyPresetWithMode] Invalid image size:", imageWidth, imageHeight);
        return false;
      }

      const container = containerRef.current;
      if (!container) {
        console.error("[applyPresetWithMode] Container ref not available");
        return false;
      }

      const fitted = computeFittedCanvasSize(
        container.clientWidth,
        container.clientHeight,
        aspectRatio
      );
      if (!fitted) {
        console.error("[applyPresetWithMode] Could not fit canvas");
        return false;
      }

      const cw = fitted.width;
      const ch = fitted.height;
      const t = getTransform(iw, ih, cw, ch, mode === "fill" ? "fill" : "fit");

      layoutLockRef.current = true;
      layoutLockFullObjectRef.current = {
        width: t.width,
        height: t.height,
        x: t.x,
        y: t.y,
        imageW: iw,
        imageH: ih,
      };
      canvasSizeRef.current = { width: cw, height: ch };
      objectSizeRef.current = { width: t.width, height: t.height };
      objectPositionRef.current = { x: t.x, y: t.y };
      originalImageSizeRef.current = { width: iw, height: ih };
      cropStateRef.current = { x: 0, y: 0, width: iw, height: ih };
      cropExtentRef.current = { x: 0, y: 0, width: iw, height: ih };
      setCanvasSize({ width: cw, height: ch });
      setObjectSize({ width: t.width, height: t.height });
      setObjectPosition({ x: t.x, y: t.y });
      objectTightBoundsRef.current = false;

      // Full-frame first (FILL/FIT), then retighten if cutout alpha is already ready
      setCropState({ x: 0, y: 0, width: iw, height: ih });
      setOriginalImageSize({ width: iw, height: ih });
      retightenSubjectUnderLayoutLock();
      consumePendingSubjectPlacement(placementLayout(), { force: true });

      console.log(
        `[applyPresetWithMode] mode=${mode} canvas=${cw}x${ch} object=${Math.round(objectSizeRef.current.width)}x${Math.round(objectSizeRef.current.height)} at (${Math.round(objectPositionRef.current.x)},${Math.round(objectPositionRef.current.y)})`
      );
      return true;
    },
    /**
     * Fit the subject inside a padded content area (% insets on each canvas edge).
     */
    applyPaddingLayout: (padding) => {
      const cw = canvasSizeRef.current.width;
      const ch = canvasSizeRef.current.height;
      if (!cw || !ch) {
        console.error("[applyPaddingLayout] Canvas size not available");
        return false;
      }

      const crop = cropStateRef.current;
      const orig = originalImageSizeRef.current;
      const iw = crop.width > 0 ? crop.width : orig.width;
      const ih = crop.height > 0 ? crop.height : orig.height;
      if (!iw || !ih) {
        console.error("[applyPaddingLayout] Image size not available");
        return false;
      }

      const content = computePaddedImageTransform(cw, ch, iw, ih, padding);
      if (content.width <= 0 || content.height <= 0) {
        console.error("[applyPaddingLayout] Invalid content area");
        return false;
      }

      const nextCanvasW = content.canvasWidth || cw;
      const nextCanvasH = content.canvasHeight || ch;
      const nextX = content.x;
      const nextY = content.y;
      const nextCornerRadius = content.cornerRadiusPx || 0;

      layoutLockRef.current = true;
      layoutLockFullObjectRef.current = {
        width: content.width,
        height: content.height,
        x: nextX,
        y: nextY,
        imageW: iw,
        imageH: ih,
      };
      canvasSizeRef.current = { width: nextCanvasW, height: nextCanvasH };
      objectSizeRef.current = { width: content.width, height: content.height };
      objectPositionRef.current = { x: nextX, y: nextY };
      setCanvasSize({ width: nextCanvasW, height: nextCanvasH });
      setCanvasCornerRadius(nextCornerRadius);
      setObjectCornerRadii(
        content.imageCornerRadii || { tl: 0, tr: 0, br: 0, bl: 0 }
      );
      setObjectSize({ width: content.width, height: content.height });
      setObjectPosition({ x: nextX, y: nextY });
      consumePendingSubjectPlacement(placementLayout(), { force: true });
      return true;
    },
    setLayoutLock: (locked) => {
      layoutLockRef.current = Boolean(locked);
      if (!locked) {
        layoutLockFullObjectRef.current = null;
      }
    },
    exportCompositePng: async ({ exportSize = "standard" } = {}) => {
      const size = canvasSizeRef.current.width
        ? { ...canvasSizeRef.current }
        : canvasSize;
      return compositeEditorToPngBlob({
        imageUrl,
        backgroundColor: backgroundLayerVisible ? backgroundColor : "transparent",
        backgroundImageUrl: backgroundLayerVisible ? backgroundImageUrl : null,
        canvasSize: size,
        objectSize: { ...objectSizeRef.current },
        objectPosition: { ...objectPositionRef.current },
        objectRotation: objectRotationRef.current,
        objectFlip: { ...objectFlipRef.current },
        cropState: { ...cropStateRef.current },
        shadowState,
        outlineState,
        outlineImageUrl,
        outlineBleedPx,
        resolutionLabel,
        exportSize,
        // Always draw the full PNG aligned to the opaque crop box. AI / Cloudinary
        // shadows live in pixels *outside* that box; cropping them off is why
        // downloads looked like a flat cutout. Transparent pixels are a no-op.
        softOverflow: true,
        objectLayerVisible,
        layers: normalizeLayers(useEditorStore.getState().layers),
        watermarkState: useEditorStore.getState().object?.watermark || null,
      });
    },
    isSubjectReady: () => {
      const size = canvasSizeRef.current;
      return (
        canvasReadyRef.current &&
        imageLoadedRef.current &&
        size.width > 0 &&
        size.height > 0
      );
    },
    setPendingSubjectPlacement: (placement) => {
      if (!pendingSubjectPlacementRef) return;
      pendingSubjectPlacementRef.current = placement || null;
    },
    flushPendingSubjectPlacement: (expectedUrl) => {
      if (!pendingSubjectPlacementRef?.current) return false;
      if (expectedUrl && imageUrl !== expectedUrl) return false;
      if (!imageLoadedRef.current) return false;
      return consumePendingSubjectPlacement(placementLayout(), { force: false });
    },
  }));

}
